import { useState, HTMLAttributes } from "preact/compat"
import { Button } from "@/components/ui/button"
import { Upload, Loader2, FileText, X } from "lucide-react"
import { useLocation } from "wouter-preact"
import { DetailNavbar } from "@/app/navbar.tsx"
import { useDropzone } from "react-dropzone"
import { toast } from "@/components/ui/use-toast.ts"
import { NOT_A_CHECK_PDF_TOAST } from "./check_upload_messages.ts"
import { api_fetch } from "@/api"
import { CheckDetail, ParseCheckResponse } from "@/api/accounting.tsx"
import { format, parseISO } from "date-fns"
import { exactCurrencyFormatter } from "@/utils"
import { check_detail_path } from "./check_path"

export function NewCheck() {
  const [, setLocation] = useLocation()
  const [checkNumber, setCheckNumber] = useState("")
  const [checkDate, setCheckDate] = useState("")
  const [checkAmount, setCheckAmount] = useState("")
  const [uploading, setUploading] = useState(false)
  const [creating, setCreating] = useState(false)

  // Parsed check state
  const [parsedCheck, setParsedCheck] = useState<ParseCheckResponse | null>(null)

  async function handleUpload(files: File[]) {
    if (files.length === 0) return

    const file = files[0]
    if (!file.type.includes("pdf")) {
      toast({
        variant: "destructive",
        title: "Invalid file type",
        description: "Please upload a PDF file",
      })
      return
    }

    setUploading(true)
    try {
      const formData = new FormData()
      formData.append("file", file)

      const response = await fetch("/api/check/parse", {
        method: "POST",
        credentials: "include",
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || "Failed to parse check")
      }

      const check: CheckDetail = await response.json()
      setLocation(`/accounting/check/${check.id}`)
      toast({
        title: "Check parsed successfully",
      })
    } catch (error) {
      console.error("Failed to parse check:", error)
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to parse check file",
      })
    } finally {
      setUploading(false)
    }
  }

  function handleClearParsedCheck() {
    setParsedCheck(null)
    setCheckNumber("")
    setCheckDate("")
    setCheckAmount("")
  }

  async function handleCreate() {
    if (!checkNumber.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Check number is required",
      })
      return
    }

    setCreating(true)
    try {
      const res = await api_fetch("/check", {
        method: "POST",
        body: {
          number: checkNumber.trim(),
          date: checkDate || null,
          amount: checkAmount ? parseFloat(checkAmount) : null,
        },
      })

      if (!res.ok) {
        throw new Error("Failed to create check")
      }

      toast({
        title: "Success",
        description: "Check created successfully",
      })

      // Navigate to the check detail page
      setLocation(check_detail_path(checkNumber.trim()))
    } catch (error) {
      console.error("Failed to create check:", error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create check",
      })
    } finally {
      setCreating(false)
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleUpload,
    onDropRejected: () => toast(NOT_A_CHECK_PDF_TOAST),
    multiple: false,
    accept: {
      "application/pdf": [".pdf"],
    },
  })

  return (
    <div class="w-full h-screen flex flex-col">
      <DetailNavbar />
      {/* Header - matches check detail view style */}
      <div class="flex items-end justify-between mt-1 mb-3 mx-4 gap-4 shrink-0">
        <div>
          <div class="flex flex-col mt-1 mb-2">
            {/* Amount - large editable field */}
            <div class="flex items-center">
              <span class="text-3xl font-semibold text-gray-400">$</span>
              <input
                type="text"
                class="text-3xl font-semibold border-0 border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 p-0 outline-none focus:ring-0 bg-transparent w-40"
                placeholder="0.00"
                value={checkAmount}
                onChange={e => setCheckAmount(e.currentTarget.value)}
              />
            </div>
          </div>
          {/* Date */}
          <input
            type="date"
            class="text-gray-500 border-0 border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 p-0 outline-none focus:ring-0 bg-transparent"
            value={checkDate}
            onChange={e => setCheckDate(e.currentTarget.value)}
          />
          {/* Check number */}
          <div class="flex gap-8 items-center">
            <input
              type="text"
              class="text-3xl text-primary border-0 border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 p-0 outline-none focus:ring-0 bg-transparent"
              placeholder="Check #"
              value={checkNumber}
              onChange={e => setCheckNumber(e.currentTarget.value)}
            />
          </div>
          <h3 class="text-gray-500">{parsedCheck?.meta.source ? `${parsedCheck.meta.source} Check` : "New Check"}</h3>
        </div>
        <div class="flex items-center gap-2 shrink-0">
          <Button onClick={handleCreate} disabled={creating || !checkNumber.trim()}>
            {creating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              "Create Check"
            )}
          </Button>
        </div>
      </div>

      <div class="flex w-full px-4 gap-2 flex-1 min-h-0">
        {/* Left pane - Parsed Lines Table */}
        <div class="flex-1 flex flex-col min-h-0">
          <div class="mx-2 mt-2 flex items-center justify-between border-b border-gray-200">
            <div class="flex h-auto bg-transparent p-0 rounded-none">
              <div class="rounded-none border-b-2 border-blue-600 px-4 py-2 text-blue-600">Check Lines</div>
            </div>
            {parsedCheck && (
              <div class="text-sm text-gray-500">
                {parsedCheck.rows.length} line{parsedCheck.rows.length !== 1 ? "s" : ""}
              </div>
            )}
            {parsedCheck && (
              <button
                onClick={handleClearParsedCheck}
                class="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 px-2">
                <X className="w-4 h-4" />
                Clear
              </button>
            )}
          </div>

          <div class="flex-1 overflow-auto mt-2">
            {parsedCheck && parsedCheck.rows.length > 0 ? (
              <div class="border border-gray-200 rounded-lg w-full overflow-hidden">
                <table class="w-full border-collapse text-sm" style={{ tableLayout: "auto" }}>
                  <thead class="bg-slate-50">
                    <tr class="border-b border-gray-200">
                      <th class="px-2 py-2 text-left text-sm font-medium text-gray-900 whitespace-nowrap">#</th>
                      <th class="px-2 py-2 text-left text-sm font-medium text-gray-900 whitespace-nowrap">Invoice #</th>
                      <th class="px-2 py-2 text-left text-sm font-medium text-gray-900 whitespace-nowrap">Date</th>
                      <th class="px-2 py-2 text-left text-sm font-medium text-gray-900 whitespace-nowrap">
                        Description
                      </th>
                      <th class="px-2 py-2 text-right text-sm font-medium text-gray-900 whitespace-nowrap">Gross</th>
                      <th class="px-2 py-2 text-right text-sm font-medium text-gray-900 whitespace-nowrap">Discount</th>
                      <th class="px-2 py-2 text-right text-sm font-medium text-gray-900 whitespace-nowrap">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedCheck.rows.map((row, idx) => (
                      <tr key={idx} class="hover:bg-slate-50 border-b border-gray-200">
                        <td class="px-2 py-2 whitespace-nowrap">{idx + 1}</td>
                        <td class="px-2 py-2 whitespace-nowrap font-mono">{row.invoice_number}</td>
                        <td class="px-2 py-2 whitespace-nowrap">
                          {row.invoice_date ? format(parseISO(row.invoice_date), "PP") : "-"}
                        </td>
                        <td class="px-2 py-2 max-w-xs truncate">{row.description || "-"}</td>
                        <td class="px-2 py-2 text-right whitespace-nowrap">
                          {row.gross_amount != null ? exactCurrencyFormatter(row.gross_amount) : "-"}
                        </td>
                        <td class="px-2 py-2 text-right whitespace-nowrap">
                          {row.discount_amount != null ? exactCurrencyFormatter(row.discount_amount) : "-"}
                        </td>
                        <td class="px-2 py-2 text-right font-medium whitespace-nowrap">
                          {exactCurrencyFormatter(row.invoice_amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot class="bg-slate-50 border-t border-gray-200">
                    <tr>
                      <td colSpan={6} class="px-2 py-2 text-right font-medium text-gray-700">
                        Total
                      </td>
                      <td class="px-2 py-2 text-right font-bold text-gray-900">
                        {exactCurrencyFormatter(parsedCheck.rows.reduce((sum, row) => sum + row.invoice_amount, 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div class="flex items-center justify-center h-full">
                <div class="text-center text-gray-400 flex flex-col items-center">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>
                    Create the check by filling out check amount, check date, and check number before creating lines.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right pane - File upload (matches detail view width) */}
        <div class="w-1/2 flex flex-col bg-white">
          <div class="mx-2 mt-2 h-auto bg-transparent p-0 border-b border-gray-200 rounded-none shrink-0">
            <div class="rounded-none border-b-2 border-blue-600 px-4 py-2 text-blue-600 inline-block">Check PDF</div>
          </div>
          <div class="flex-1 min-h-0 flex items-center justify-center p-4">
            <div
              {...(getRootProps() as HTMLAttributes<HTMLDivElement>)}
              class={`flex flex-col items-center w-full max-w-md border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"
              }`}>
              <input {...getInputProps()} />
              {uploading ? (
                <>
                  <Loader2 className="mx-auto h-12 w-12 text-gray-400 mb-4 animate-spin" />
                  <p class="text-lg font-medium text-gray-900 mb-2">Parsing check...</p>
                  <p class="text-sm text-gray-500">This may take a moment</p>
                </>
              ) : (
                <>
                  <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p class="text-lg font-medium text-gray-900 mb-2">
                    {isDragActive ? "Drop the PDF here" : "Upload check PDF"}
                  </p>
                  <p class="text-sm text-gray-500">Drag and drop a PDF file to upload, or click to select</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
